import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { SearchIcon } from "lucide-react";

import { cn } from "../lib/cn";

export type CommandProps = React.ComponentProps<typeof CommandPrimitive>;
/** A scoped adaptation of shadcn's current new-york-v4 command primitive. */
export const Command = ({ className, ...props }: CommandProps) => (
  <CommandPrimitive
    className={cn(
      "flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground",
      className,
    )}
    data-slot="command"
    {...props}
  />
);

export type CommandInputProps = React.ComponentProps<typeof CommandPrimitive.Input>;
/** Search field of a {@link Command}, with the upstream leading search affordance. */
export const CommandInput = ({ className, ...props }: CommandInputProps) => (
  <div className="flex h-9 items-center gap-2 border-b px-3" data-slot="command-input-wrapper">
    <SearchIcon aria-hidden="true" className="size-4 shrink-0 opacity-50" />
    <CommandPrimitive.Input
      className={cn(
        "flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      data-slot="command-input"
      {...props}
    />
  </div>
);

export type CommandListProps = React.ComponentProps<typeof CommandPrimitive.List>;
/** Scrolling result region of a {@link Command}. */
export const CommandList = ({ className, ...props }: CommandListProps) => (
  <CommandPrimitive.List
    className={cn("max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto", className)}
    data-slot="command-list"
    {...props}
  />
);

export type CommandEmptyProps = React.ComponentProps<typeof CommandPrimitive.Empty>;
/** Message shown when a {@link Command} search matches nothing. */
export const CommandEmpty = ({ className, ...props }: CommandEmptyProps) => (
  <CommandPrimitive.Empty
    className={cn("py-6 text-center text-sm text-muted-foreground", className)}
    data-slot="command-empty"
    {...props}
  />
);

export type CommandGroupProps = React.ComponentProps<typeof CommandPrimitive.Group>;
/** Labeled section of {@link CommandItem}s. */
export const CommandGroup = ({ className, ...props }: CommandGroupProps) => (
  <CommandPrimitive.Group
    className={cn(
      "overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground",
      className,
    )}
    data-slot="command-group"
    {...props}
  />
);

export type CommandSeparatorProps = React.ComponentProps<typeof CommandPrimitive.Separator>;
/** Divider between {@link CommandGroup}s. */
export const CommandSeparator = ({ className, ...props }: CommandSeparatorProps) => (
  <CommandPrimitive.Separator
    className={cn("-mx-1 h-px bg-border", className)}
    data-slot="command-separator"
    {...props}
  />
);

export type CommandItemProps = React.ComponentProps<typeof CommandPrimitive.Item>;
/** One selectable row of a {@link Command}. */
export const CommandItem = ({ className, ...props }: CommandItemProps) => (
  <CommandPrimitive.Item
    className={cn(
      "relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
      className,
    )}
    data-slot="command-item"
    {...props}
  />
);

export type CommandShortcutProps = React.ComponentProps<"span">;
/** Trailing keyboard hint of a {@link CommandItem}. */
export const CommandShortcut = ({ className, ...props }: CommandShortcutProps) => (
  <span
    className={cn("ml-auto text-xs tracking-widest text-muted-foreground", className)}
    data-slot="command-shortcut"
    {...props}
  />
);
